/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
    shape,
    array,
    string,
    bool,
} from 'prop-types';
import mbpLogger from '@module-logger/mbp';

/* eslint-disable import/no-cycle */
import { getIsBot } from '../../../../../state/ducks/App/App-Selectors';
import useExperience from '../../../../helpers/Personalization/useExperience';
import GenericSkeleton from '../../GraphqlSkeletonComponents/GenericSkeleton';
import FallbackContentContainer from '../Common/FallbackContentContainer';
import HomePageBlock from '../../GraphqlHomePage/HomePageBlock';

const PersonalizedExperienceContentBlock = ({
    data: {
        content_type: contentType = '',
        content_types,
        pz_campaign_id: interactionName = '',
        content_zones,
        skeleton_height,
        skeleton_width,
        skeleton_margin = '0 auto',
        fallback_uid: fallbackUID = '',
    },
    ssrDeviceType,
    presentationFamily,
    brand,
    hasTopMargin,
}) => {
    const [showSkeleton, setShowSkeleton] = useState(true);
    const isBot = useSelector(getIsBot);
    if (isBot) {
        return (
            <FallbackContentContainer
                skeleton_height={skeleton_height}
                skeleton_width={skeleton_width}
                fallbackUID={fallbackUID}
                contentType={contentType}
                hasTopMargin={hasTopMargin}
            />
        );
    }

    if (typeof window === 'undefined') {
        return (
            <GenericSkeleton
                height={skeleton_height}
                width={skeleton_width}
                maxWidth="unset"
                margin={skeleton_margin}
                id="PersonalizedExperienceContentBlock"
            />
        );
    }

    const targeting = {
        pageType: 'home',
        ...(content_zones?.length && content_zones?.[0] && {
            contentZones: content_zones,
        }),
    };

    const {
        loading,
        error,
        data,
        variables,
    } = useExperience({
        interactionName,
        targeting,
        type: 'content',
        resolveIdentity: true,
        queryName: `Personalized_${contentType}`,
    });

    if (loading || variables?.skip) {
        return (
            <GenericSkeleton
                height={skeleton_height}
                width={skeleton_width}
                maxWidth="unset"
                margin={skeleton_margin}
            />
        );
    }

    if (error) {
        mbpLogger.logError({
            function: `PersonalizedExperienceContentBlock - content_types = ${content_types.toString()} interactionName = ${interactionName}`,
            message: 'Error loading API data',
            appName: process.env.npm_package_name,
            module: 'PersonalizedExperienceContentBlock',
            jsError: error,
        });
        return (
            <FallbackContentContainer
                skeleton_height={skeleton_height}
                skeleton_width={skeleton_width}
                fallbackUID={fallbackUID}
                contentType={contentType}
                hasTopMargin={hasTopMargin}
            />
        );
    }

    const content = data?.experience?.content;
    const experiment = data?.experience?.experiment;
    const experimentTracking = data?.experience?.experimentTracking;

    if (!content) {
        return (
            <FallbackContentContainer
                skeleton_height={skeleton_height}
                skeleton_width={skeleton_width}
                fallbackUID={fallbackUID}
                contentType={contentType}
                hasTopMargin={hasTopMargin}
                experimentTracking={experimentTracking}
            />
        );
    }

    const expContentType = experiment?.value?.contentRecipe?.contentType || contentType;

    if (!expContentType || !content_types?.includes(expContentType)) {
        return null;
    }

    const block = {
        [expContentType]: content,
    };

    return (
        <>
            {showSkeleton && (
                <GenericSkeleton
                    height={skeleton_height}
                    width={skeleton_width}
                    id={expContentType}
                    maxWidth="unset"
                    margin="0 auto"
                />
            )}

            <HomePageBlock
                block={block}
                experimentTracking={experimentTracking}
                ssrDeviceType={ssrDeviceType}
                presentationFamily={presentationFamily}
                brand={brand}
                hasTopMargin={hasTopMargin}
                setShowSkeleton={setShowSkeleton}
                isFromExperienceBlock
            />
        </>
    );
};

export default PersonalizedExperienceContentBlock;

PersonalizedExperienceContentBlock.propTypes = {
    data: shape({
        content_type: string,
        content_types: array.isRequired,
        pz_campaign_id: string,
        content_zones: array,
        skeleton_height: string,
        skeleton_width: string,
        skeleton_margin: string,
        fallback_uid: string,
    }).isRequired,
    ssrDeviceType: string,
    presentationFamily: string,
    brand: string,
    hasTopMargin: bool,
};

PersonalizedExperienceContentBlock.defaultProps = {
    ssrDeviceType: '',
    presentationFamily: '',
    brand: '',
    hasTopMargin: false,
};
